// Native Terminstornierung (einzelner Termin, ggf. Serienmitglied) — Web bleibt Quelle für
// Mandanten-Konfiguration der Absagegründe; hier werden nur die Standardgründe genutzt.

import { updateSession } from "./sessions.js";
import { isCancelledSession, SESSION_STATUS } from "./session-status.js";
import { isPastConfirmedExecutionSession } from "./session-delete-policy.js";
import {
  resolveSessionCancelReasonEntries,
  resolveSessionCancelReasonLabel,
} from "./session-cancel-reasons.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

/** Fallback-Status wenn Ausfallgrund optional und leer gelassen wird. */
export const SESSION_CANCEL_REASON_FALLBACK = SESSION_STATUS.CANCELLED_OTHER;

/**
 * Absage-Updates. Default: kein Nachhol-Backlog (`makeup_waived_at`).
 * Leerer Grund → `cancelled_other` (für optionale Ausfallgründe).
 * @param {{
 *   reason?: string|null,
 *   note?: string|null,
 *   planMakeup?: boolean,
 *   now?: Date,
 * }} input
 */
export function buildSessionCancelUpdates(input = {}) {
  const r = __asStr(input?.reason) || SESSION_CANCEL_REASON_FALLBACK;
  const now =
    input?.now instanceof Date && Number.isFinite(input.now.getTime())
      ? input.now
      : new Date();
  const planMakeup = Boolean(input?.planMakeup);
  const updates = {
    attendance_confirmed: false,
    status: r,
    cancelled_reason: r,
    cancelled_note: __asStr(input?.note) || null,
    cancelled_at: now.toISOString(),
  };
  if (planMakeup) {
    updates.makeup_waived_at = null;
  } else {
    updates.makeup_waived_at = now.toISOString();
  }
  return updates;
}

/**
 * @param {object|null|undefined} session
 * @returns {boolean}
 */
export function isMobileSessionCancellable(session) {
  if (!session) return false;
  if (isCancelledSession(session)) return false;
  if (__asStr(session?.trainer_settlement_id)) return false;
  if (isPastConfirmedExecutionSession(session)) return false;
  return true;
}

/**
 * @param {(key: string) => string} t
 * @returns {Array<{ id: string, label: string }>}
 */
export function buildMobileSessionCancelReasonOptions(t) {
  return resolveSessionCancelReasonEntries(null).map((entry) => ({
    id: entry.id,
    label: resolveSessionCancelReasonLabel(entry, t),
  }));
}

/**
 * @param {string} sessionId
 * @param {{ reason: string, note?: string, planMakeup?: boolean, session?: Record<string, unknown>|null }} input
 */
export async function cancelMobilePlanningSession(
  sessionId,
  { reason, note, planMakeup = false, session } = {},
) {
  const sid = __asStr(sessionId);
  const r = __asStr(reason);
  if (!sid)
    return { ok: false, errorKey: "mobile.trainingsdesk.session.notFound" };
  if (session && !isMobileSessionCancellable(session)) {
    return {
      ok: false,
      errorKey: "mobile.trainingsdesk.session.cancel.blockedHint",
    };
  }
  if (r) {
    const allowed = resolveSessionCancelReasonEntries(null).some(
      (entry) => entry.id === r,
    );
    if (!allowed) {
      return {
        ok: false,
        errorKey: "mobile.trainingsdesk.session.cancel.errorReason",
      };
    }
  }

  const result = await updateSession(
    sid,
    buildSessionCancelUpdates({ reason: r, note, planMakeup }),
  );
  if (!result?.ok) {
    return {
      ok: false,
      errorKey: "mobile.trainingsdesk.session.cancel.errorSave",
    };
  }
  return { ok: true, data: result.data };
}
